import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModuleOn } from '../../store/tenantConfig';
import { useLaundryPrint } from '../laundry';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@matix/auth';
import { getApiErrorMessage } from '@matix/api-client';
import type { Customer, CustomerAddress, CreateOrderRequest, CreateOrderResponse } from '@matix/types';
import { roundMoney } from '@matix/utils';
import { useUI } from '../../store/ui';
import { useCart, useCartTotals } from '../../store/cart';
import { amountStep, moneyPrecision } from '../../store/currency';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { fmt } from '../../lib/mock-data';
import { useTaxConfig } from '../../store/taxConfig';
import { buildCreateOrderRequest, usePlaceOrder } from '../orders/usePlaceOrder';
import { printAfterCharge } from '../print';
import { usePaymentTypesWarm } from '../modals/usePaymentTypes';
import { useGiftCards } from '../../store/giftCards';
import { CustomerPicker } from './CustomerPicker';
import { AddressPicker } from './AddressPicker';
import { DriverSelect } from './DriverSelect';
import { DeliveryTimePicker } from './DeliveryTimePicker';
import { formatDraftDateTime } from './deliveryTime';
import { useCustomerDetail } from './hooks';
import { autoDeliveryFee, buildDeliveryPayload, deliveryFeeOf } from './deliveryPayload';
import { useInvoiceSources } from '../../store/invoiceSources';
import {
  useParkedOrdersStore,
  buildSnapshotFromCart,
  summarizeForCard,
} from '../parked-orders';
import { XIcon } from '../../components/icons';
import { cashierIdentity } from '../../lib/cashierIdentity';

/**
 * DeliveryConfirmModal — replaces the regular Confirm Order modal when
 * orderMode === 'delivery'. Layout follows spec section 5.2:
 *
 *   Title:  "Confirm Order"
 *   ┌────────────────────────────────────────────┐
 *   │ Customer    [search / chip + Change]       │
 *   │ Address     [radio list + Add]             │
 *   │ Reference No  ___________________          │
 *   │ Delivery Date  [date]   [time]             │
 *   │ Delivery Fees [_]   Driver [select v]      │
 *   ├────────────────────────────────────────────┤
 *   │ [HOLD] [CANCEL]                  [CREATE]  │
 *   └────────────────────────────────────────────┘
 *
 * CREATE submits POST /api/v1/orders with order_type=2 and the delivery
 * sub-object. The server is authoritative on totals; cart math here is
 * for display only and the receipt response drives the print flow.
 */
export function DeliveryConfirmModal() {
  const isOpen = useUI((s) => s.openModal === 'deliveryConfirm');
  const closeModal = useUI((s) => s.closeModal);
  const showModal = useUI((s) => s.showModal);

  const taxPercent = useTaxConfig((s) => s.config.sales_percentage);
  const taxLabel = useTaxConfig((s) => s.config.label_en);
  const lines = useCart((s) => s.lines);
  const draft = useCart((s) => s.deliveryDraft);
  const cartCustomer = useCart((s) => s.customer);
  const setCartCustomer = useCart((s) => s.setCustomer);
  const setDraft = useCart((s) => s.setDeliveryDraft);
  const clearDraft = useCart((s) => s.clearDeliveryDraft);
  const reset = useCart((s) => s.reset);

  const { cashier } = useAuth();
  const { t } = useLang();
  const queryClient = useQueryClient();
  const lastReqRef = useRef<CreateOrderRequest | null>(null);

  // Live customer detail (with addresses) for the selected customerId. Drives
  // the address picker. Fetched fresh whenever the cashier picks a customer
  // so newly-added addresses are visible (cache invalidated by AddressPicker
  // on add).
  const customerDetail = useCustomerDetail(draft.customerId);

  // Local state for the selected customer card (kept separate from the draft
  // so the picker can show name/phone without joining on every render).
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Sync selectedCustomer with the cart customer when the modal opens.
  // The cashier may have already attached a customer via the cart-header
  // chip before clicking "Create delivery" — we honour that selection so
  // they don't get prompted twice.
  useEffect(() => {
    if (!isOpen) return;
    if (draft.customerId == null && cartCustomer && cartCustomer.type === 'cash') {
      setDraft({ customerId: cartCustomer.id, addressId: null });
      setSelectedCustomer(cartCustomer);
      return;
    }
    if (customerDetail.data && customerDetail.data.id === draft.customerId) {
      setSelectedCustomer(customerDetail.data);
    } else if (draft.customerId == null) {
      setSelectedCustomer(null);
    }
  }, [isOpen, customerDetail.data, draft.customerId, cartCustomer, setDraft]);

  // Picking an address also sets the fee from its delivery zone, the way the
  // legacy till did (see autoDeliveryFee). An order reopened for editing keeps
  // the fee it was sold with.
  const editingReceiptId = useCart((s) => s.editingReceiptId);
  const selectAddress = useCallback(
    (address: CustomerAddress) => {
      if (editingReceiptId != null) {
        setDraft({ addressId: address.id });
        return;
      }
      setDraft({
        addressId: address.id,
        deliveryFees: autoDeliveryFee(address, useInvoiceSources.getState().deliveryFee()),
      });
    },
    [editingReceiptId, setDraft],
  );

  // Pre-select the most recent address (DESC order from the API) when a
  // customer is loaded and no address is yet chosen for this customer.
  useEffect(() => {
    if (!selectedCustomer) return;
    if (draft.addressId != null) return;
    // Wait for the customer DETAIL: it is what carries each address's fee, and
    // an address pre-selected from the search row would lock in a fee of 0.
    if (customerDetail.data?.id !== selectedCustomer.id) return;
    const first = customerDetail.data.addresses?.[0];
    if (first) selectAddress(first);
  }, [selectedCustomer, customerDetail.data, draft.addressId, selectAddress]);

  // ── Idempotency key per modal open ───────────────────────────────────
  const [idempotencyKey, setIdempotencyKey] = useState(() => safeUuid());
  useEffect(() => {
    if (isOpen) setIdempotencyKey(safeUuid());
  }, [isOpen]);

  // ── Totals — recompute with the live delivery_fees from the draft. ──
  const totals = useCartTotals();

  // ── The tender a delivery receipt is booked on at CREATE ──────────────
  //
  // The money is taken twenty minutes later, at hand-over, on the Delivery
  // workspace. So the receipt is written the way the call centre writes its
  // own: on the Unpaid sentinel (Code −1) when the tenant has one — which is
  // exactly what puts the order in the "Awaiting payment" queue — else on the
  // cash method, else on Code 1.
  //
  // This used to be a hardcoded `type_code: 1`. Inv_PaymentType codes are
  // per tenant, and on one of them Code 1 is the GIFT CARD tender — so every
  // delivery order there was rejected with GIFT_CARD_TENDER_UNBACKED although
  // no card was involved. The gift-card code is excluded from every fallback
  // for the same reason.
  const paymentTypes = usePaymentTypesWarm({ enabled: isOpen });
  const giftCardCode = useGiftCards((s) => s.config.payment_type_code);
  const pendingTender = useMemo(() => {
    const all = (paymentTypes.data?.payment_types ?? []).filter((p) => p.code !== giftCardCode);
    return (
      all.find((p) => p.code === -1) ??
      all.find((p) => p.is_cash) ??
      all.find((p) => p.code === 1) ??
      all.find((p) => p.code > 0) ??
      null
    );
  }, [paymentTypes.data, giftCardCode]);

  // LAUNDRY: a delivery order is TAKEN IN (held, paid when it is delivered),
  // and prints the laundry ticket + bag tag with the order barcode.
  const laundryOn = useModuleOn('laundry');
  const printLaundryTicket = useLaundryPrint();

  // ── Place-order mutation ─────────────────────────────────────────────
  const placeOrder = usePlaceOrder({
    onSuccess: (response: CreateOrderResponse) => {
      toast(t({ en: `Delivery #${response.entry_no} saved`, ar: `تم حفظ التوصيل #${response.entry_no}` }), 'success');
      const req = lastReqRef.current;
      if (laundryOn && req?.hold) {
        void printLaundryTicket(response.order_id);
      } else if (req && cashier) {
        // printAfterCharge runs fire-and-forget — print failures shouldn't
        // block the success path (the receipt is already saved).
        printAfterCharge({
          queryClient,
          response,
          request: req,
          cartLines: lines,
          customer: selectedCustomer,
          // Delivery orders don't take a table; pass null so the receipt
          // template suppresses the table row.
          table: null,
          tenantDb: cashier.tenant_db ?? null,
          cashier: cashierIdentity(cashier),
          paymentTypes: [],
        });
      }
      reset();
      clearDraft();
      closeModal();
    },
    onError: (err) => {
      toast(getApiErrorMessage(err) || t({ en: 'Failed to save delivery order', ar: 'تعذّر حفظ طلب التوصيل' }), 'error');
    },
  });

  // What the FORM needs before the order can be booked at all — driver is
  // optional (dispatchers commonly assign it after the order is taken) and so
  // is the ADDRESS: a customer with none on file still books, and the address
  // can be added to them afterwards. Required: cart not empty + customer +
  // valid date (default 'now' always satisfies).
  const formReady =
    lines.length > 0 && draft.customerId != null && !placeOrder.isPending;
  // "Create — pay later" books the receipt here, so it additionally needs a
  // tender to book it ON. "Take payment" does not: PaymentModal loads the
  // tenant's payment types itself and the cashier picks from them.
  const canCreate = formReady && pendingTender != null;
  const canTakePayment = formReady;

  function handleCreate() {
    if (!cashier || !canCreate || !pendingTender) return;
    const deliveryPayload = buildDeliveryPayload(draft);
    if (!deliveryPayload) return;
    // One split for the full computed total on the pending tender (see above).
    // Server will recompute and reject if the cart drifted.
    const orderTotal = round2(totals.total);
    const req = buildCreateOrderRequest({
      idempotencyKey,
      branchId: cashier.branch_id,
      cartLines: lines,
      customer: selectedCustomer,
      payments: [{ type_code: pendingTender.code, amount: orderTotal }],
      // The SAME fee that rides in the delivery sub-object. The server prices
      // a delivery order from that sub-object (orders.service: order_type 2 →
      // `delivery.delivery_fees`), but the builder needs it too — it is what
      // `expected_subtotal / expected_tax / expected_total` are computed from.
      // Passing 0 here left expected_total short by exactly the fee and the
      // server refused the order with
      //   "Order total mismatch: client says 7, server computed 9"
      // on every delivery carrying one.
      deliveryFee: deliveryFeeOf(draft),
      delivery: deliveryPayload,
      orderTypeCode: 2,
    });
    // A laundry tenant takes the order in as a HELD invoice (the server allows
    // a held delivery order only when the laundry module is on).
    if (laundryOn) req.hold = true;
    lastReqRef.current = req;
    placeOrder.mutate(req);
  }

  /**
   * Hand the finished delivery form to the ordinary payment flow.
   *
   * A delivery is not always collected at hand-over: plenty are paid at the
   * counter when they are ordered, on a card, or split across two methods.
   * Rather than grow a second payment UI inside this modal, we close it and
   * open the one the till already has — which brings splits, change due,
   * gift cards, loyalty, the hosted gateways and the Unpaid CTA with it.
   *
   * Nothing is handed over explicitly: the delivery form lives in the CART
   * (`deliveryDraft`), so PaymentModal reads the same rows this modal wrote
   * and rebuilds the sub-object from `buildDeliveryPayload`. Backing out of
   * the payment modal therefore loses nothing — pressing Charge again reopens
   * this form exactly as it was left.
   */
  function handleTakePayment() {
    if (!canTakePayment) return;
    // LAUNDRY pay-now for a delivery: paid now, still a laundry order to clean,
    // send out and hand over. The promised delivery time is its ready-by; the
    // charge keeps order type 2 (usePlaceOrder) and prints the laundry ticket.
    if (laundryOn) useCart.getState().setLaundryPayNow({ readyBy: draft.deliveryDate });
    showModal('payment');
  }

  async function handleHold() {
    if (lines.length === 0) {
      toast(t({ en: 'Nothing to hold', ar: 'لا يوجد ما يُعلّق' }), 'warning');
      return;
    }
    // Reuse the standard parked-orders flow but pass the delivery draft
    // through the snapshot so resume can rehydrate the modal.
    const snapshot = buildSnapshotFromCart({
      lines: lines.map((l) => ({
        product: {
          id: l.product.id,
          code: String(l.product.id),
          name: l.product.name,
          nameAr: l.product.nameAr,
        },
        qty: l.qty,
        unitPrice: l.unitPrice,
        note: l.note ?? null,
        mods: l.mods,
      })),
      customer: selectedCustomer
        ? {
            id: selectedCustomer.id,
            type: selectedCustomer.type,
            name: selectedCustomer.name,
            name_ar: selectedCustomer.name_ar,
            phone: selectedCustomer.phone,
          }
        : null,
      delivery: { fee: round2(draft.deliveryFees || 0) },
      discount: null,
      orderType: 'delivery',
      note: null,
      deliveryDraft: {
        customer_id: draft.customerId,
        address_id: draft.addressId,
        driver_id: draft.driverId,
        delivery_fees: draft.deliveryFees,
        delivery_date: draft.deliveryDate,
        reference_no: draft.referenceNo,
      },
    });
    const summary = summarizeForCard(snapshot);
    await useParkedOrdersStore.getState().park({
      snapshot,
      note: null,
      customerLabel: summary.customerLabel,
      itemCount: summary.itemCount,
      totalValue: summary.totalValue,
    });
    toast(t({ en: `Held · ${summary.itemCount} items · ${fmt(summary.totalValue)}`, ar: `مُعلّق · ${summary.itemCount} صنف · ${fmt(summary.totalValue)}` }), 'success');
    reset();
    clearDraft();
    closeModal();
  }

  function handleCancel() {
    closeModal();
  }

  if (!isOpen) return null;

  return (
    <div className="modal-overlay show" onClick={handleCancel}>
      <div
        className="modal delivery-confirm-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={t({ en: 'Confirm delivery order', ar: 'تأكيد طلب التوصيل' })}
      >
        <header className="modal-header">
          <h2>{t({ en: 'Confirm Order', ar: 'تأكيد الطلب' })}</h2>
          <button className="icon-btn" onClick={handleCancel} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
            <XIcon size={18} />
          </button>
        </header>

        <div className="modal-body">
          <section className="modal-section">
            <h3>{t({ en: 'Customer', ar: 'العميل' })}</h3>
            <CustomerPicker
              selected={selectedCustomer}
              onSelect={(c) => {
                setSelectedCustomer(c);
                setDraft({
                  customerId: c?.id ?? null,
                  // Reset address when customer changes — old address belongs
                  // to a different customer.
                  addressId: null,
                });
                // Mirror the choice onto the cart-header chip so the cashier
                // sees the same customer everywhere. Clearing in the modal
                // also clears the cart attachment.
                setCartCustomer(c);
              }}
            />
          </section>

          {selectedCustomer && (
            <section className="modal-section">
              <h3>
                {t({ en: 'Customer Address', ar: 'عنوان العميل' })}
                <span className="section-hint">{t({ en: '(optional)', ar: '(اختياري)' })}</span>
              </h3>
              <AddressPicker
                customerId={selectedCustomer.id}
                addresses={customerDetail.data?.addresses ?? selectedCustomer.addresses ?? []}
                selectedId={draft.addressId}
                onSelect={selectAddress}
              />
            </section>
          )}

          <section className="modal-section">
            <h3>{t({ en: 'Reference No – Project No', ar: 'رقم المرجع – رقم المشروع' })}</h3>
            <input
              className="form-input"
              value={draft.referenceNo}
              onChange={(e) => setDraft({ referenceNo: e.target.value })}
              maxLength={500}
              placeholder={t({ en: '(optional)', ar: '(اختياري)' })}
            />
          </section>

          <section className="modal-section">
            <h3>
              {t({ en: 'Delivery Date & Time', ar: 'تاريخ ووقت التوصيل' })}
              <span className="delivery-time-summary">{formatDraftDateTime(draft.deliveryDate)}</span>
            </h3>
            <DeliveryTimePicker
              value={draft.deliveryDate}
              onChange={(next) => setDraft({ deliveryDate: next })}
            />
          </section>

          {paymentTypes.isSuccess && !pendingTender && (
            <p className="delivery-tender-warning" role="alert">
              {/* Only the pay-later button needs a tender to book the order
                  ON; "Take payment" goes to the payment modal, which lists the
                  tenant's methods itself. Say which one is unavailable. */}
              {t({
                en: 'No payment method is available to save this order for later. Ask the admin to enable Cash or Unpaid for the POS — or take the payment now.',
                ar: 'لا توجد طريقة دفع متاحة لحفظ هذا الطلب للدفع لاحقًا. اطلب من المسؤول تفعيل النقدي أو غير مدفوع لنقطة البيع — أو حصّل المبلغ الآن.',
              })}
            </p>
          )}

          <section className="modal-section two-col">
            <div>
              <h3>{t({ en: 'Delivery Fees', ar: 'رسوم التوصيل' })}</h3>
              <input
                type="number"
                min={0}
                step={amountStep()}
                className="form-input"
                value={draft.deliveryFees}
                onChange={(e) =>
                  setDraft({ deliveryFees: Number(e.target.value) || 0 })
                }
              />
            </div>
            <div>
              <h3>{t({ en: 'Driver', ar: 'السائق' })}</h3>
              <DriverSelect
                value={draft.driverId}
                onChange={(id) => setDraft({ driverId: id })}
              />
            </div>
          </section>

          <section className="modal-section totals-section">
            <div className="totals-row">
              <span>{t({ en: 'Subtotal', ar: 'المجموع الفرعي' })}</span>
              <span>{fmt(totals.subtotal)}</span>
            </div>
            {totals.discount > 0 && (
              <div className="totals-row">
                <span>{t({ en: 'Discount', ar: 'الخصم' })}</span>
                <span>−{fmt(totals.discount)}</span>
              </div>
            )}
            <div className="totals-row">
              <span>{taxLabel} {taxPercent}%</span>
              <span>{fmt(totals.vat)}</span>
            </div>
            <div className="totals-row">
              <span>{t({ en: 'Delivery', ar: 'التوصيل' })}</span>
              <span>{fmt(totals.deliveryFee)}</span>
            </div>
            <div className="totals-row net">
              <span>{t({ en: 'NET', ar: 'الصافي' })}</span>
              <span>{fmt(totals.total)}</span>
            </div>
          </section>
        </div>

        <footer className="modal-footer delivery-modal-footer">
          <button className="btn-hold" type="button" onClick={handleHold}>
            {t({ en: 'HOLD', ar: 'تعليق' })}
          </button>
          <button className="btn-cancel" type="button" onClick={handleCancel}>
            {t({ en: 'CANCEL', ar: 'إلغاء' })}
          </button>
          {/* Two ways to book the order, deliberately side by side:
              collect now through the till's ordinary payment flow, or save it
              for the driver to collect at hand-over — which is what CREATE
              has always done and stays the behaviour on the right-hand CTA. */}
          <button
            className="btn-create btn-create-unpaid"
            type="button"
            disabled={!canCreate}
            onClick={handleCreate}
            title={t({
              en: 'Book the order now; the money is taken at hand-over',
              ar: 'تسجيل الطلب الآن؛ يُحصّل المبلغ عند التسليم',
            })}
          >
            {placeOrder.isPending
              ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' })
              : laundryOn
                ? t({ en: 'TAKE IN — PAY ON DELIVERY', ar: 'استلام — الدفع عند التوصيل' })
                : t({ en: 'CREATE — PAY LATER', ar: 'إنشاء — دفع لاحقًا' })}
          </button>
          <button
            className="btn-create"
            type="button"
            disabled={!canTakePayment}
            onClick={handleTakePayment}
            title={t({
              en: 'Collect the money now — one method or several',
              ar: 'تحصيل المبلغ الآن — طريقة واحدة أو أكثر',
            })}
          >
            {laundryOn
              ? t({ en: 'TAKE IN — PAY NOW', ar: 'استلام — الدفع الآن' })
              : t({ en: 'TAKE PAYMENT', ar: 'تحصيل الدفع' })}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function round2(n: number): number {
  // Round money at the tenant currency's precision — one rounding rule across
  // the app (see store/cart.ts and @matix/utils/money). A hardcoded 2 turned a
  // 0.136 KWD total into 0.14.
  return roundMoney(n, moneyPrecision());
}

function safeUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for very old browsers — RFC4122 v4 hand-roll.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Date/time helpers live in ./deliveryTime.ts — they read and write the LOCAL
// clock. The `toISOString()`-based ones that used to sit here fed the time
// input UTC and composed the picked value back as local, shifting it by the
// tenant's offset on every edit.
